// Task #613: one shared rule for ordering people in the UI.
//
// Every people dropdown, picker, roster and user table in the frontend sorts
// A→Z by first name, then last name, case-insensitively. Lists are frequently
// built, merged, filtered or grouped in React before rendering, so the
// server-side ordering alone is not enough — sort with `sortPeople` (or
// `comparePeople`) wherever a people array is assembled for display.
//
// Deliberate exceptions (do NOT alphabetize): meeting attendee order, which
// the facilitator drags into a custom order, and any list where the user
// picked an explicit sort in the UI.

/**
 * The different people shapes the app renders. Some serializers expose
 * `first_name`/`last_name`, some only `full_name` or `name`, and some wrap the
 * person in a `user` (memberships, attendees, invitations).
 */
export interface PersonLike {
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
  name?: string | null
  email?: string | null
  user?: PersonLike | null
}

const clean = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

/**
 * Unwrap a nested `user` object when the outer record carries no name of its
 * own (team memberships, meeting attendees, invitations, seat holders).
 */
function resolve(person: PersonLike | null | undefined): PersonLike {
  if (!person) return {}
  const hasOwnName =
    clean(person.first_name) || clean(person.last_name) ||
    clean(person.full_name) || clean(person.name) || clean(person.email)
  if (!hasOwnName && person.user) return resolve(person.user)
  return person
}

interface NameParts {
  first: string
  last: string
  email: string
  named: boolean
}

function nameParts(person: PersonLike | null | undefined): NameParts {
  const p = resolve(person)
  const email = clean(p.email).toLowerCase()

  let first = clean(p.first_name)
  let last = clean(p.last_name)

  if (!first && !last) {
    // Fall back to a single display string: `full_name`, then `name`.
    const whole = clean(p.full_name) || clean(p.name)
    if (whole) {
      const parts = whole.split(/\s+/)
      first = parts[0] || ''
      last = parts.slice(1).join(' ')
    }
  }

  return {
    first: first.toLowerCase(),
    last: last.toLowerCase(),
    email,
    named: Boolean(first || last),
  }
}

const collate = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })

/**
 * Compare two people by first name, then last name, case-insensitively and
 * ignoring surrounding whitespace. People with no name yet (invite-by-email
 * rows) are ordered by email and sort after everyone who has a name.
 */
export function comparePeople(
  a: PersonLike | null | undefined,
  b: PersonLike | null | undefined
): number {
  const left = nameParts(a)
  const right = nameParts(b)

  if (left.named !== right.named) return left.named ? -1 : 1

  if (!left.named) return collate(left.email, right.email)

  const byFirst = collate(left.first, right.first)
  if (byFirst !== 0) return byFirst

  const byLast = collate(left.last, right.last)
  if (byLast !== 0) return byLast

  return collate(left.email, right.email)
}

/**
 * Non-mutating alphabetical sort for an array of people. Safe to call on
 * props: it always returns a new array.
 */
export function sortPeople<T extends PersonLike>(
  people: readonly T[] | null | undefined
): T[] {
  if (!people) return []
  return [...people].sort(comparePeople)
}

/**
 * Sort records whose person lives under a different key, e.g. a rock's
 * `owner`, an action item's `assignee`, or a grouped row's representative.
 */
export function sortByPerson<T>(
  records: readonly T[] | null | undefined,
  select: (record: T) => PersonLike | null | undefined
): T[] {
  if (!records) return []
  return [...records].sort((a, b) => comparePeople(select(a), select(b)))
}
